import { matchPath, useLocation } from "react-router-dom";

import { useEffect } from "react";

const SUFFIX = "Verse Medical";

// Routes whose titles are owned by per-page `useDocumentTitle` calls
// (typically order pages where the title includes the order number).
const PER_PAGE_TITLE_PATTERNS: string[] = [
  "/provider_orders/order-signed/:orderNumber",
  "/provider_orders/view_order/:orderNumber",
  "/provider_orders/order_verification/:orderNumber",
  "/provider_orders/edit/:orderNumber",
  "/provider_orders/draft/:orderNumber",
  "/provider_orders/draft/sign/:orderNumber",
];

const ROUTE_TITLES: Array<[pattern: string, title: string]> = [
  ["/contact", "Contact"],
  ["/contact_us", "Contact"],
  ["/about", "About"],
  ["/about_us", "About"],
  ["/payors", "Payor Network"],
  ["/payor-network", "Payor Network"],
  ["/payor-case-management", "Payor Case Management"],
  ["/for-providers", "For Providers"],
  ["/signature-collection", "Signature Collection"],
  ["/life-at-verse", "Life at Verse"],
  ["/jetlenses", "JetLenses"],
  ["/terms", "Terms of Service"],
  ["/terms-of-service", "Terms of Service"],
  ["/patient-rights", "Patient Rights"],
  ["/patient-rights-page", "Patient Rights"],
  ["/login", "Log In"],
  ["/password-login", "Log In"],
  ["/password/reset-link-sent", "Password Reset"],
  ["/password/request-password-reset", "Password Reset"],
  ["/password/reset", "Password Reset"],
  ["/check-your-email", "Check Your Email"],
  ["/sign-up", "Sign Up"],
  ["/careers", "Careers"],
  ["/scope-of-service/:accessToken", "Scope of Service"],
  // One title for every portal screen: a per-page one would put what the
  // patient was reading into their browser history and tab.
  ["/patient-portal/*", "Patient Portal"],
  ["/provider_orders/order-update-request/new", "Order Update Request"],
  ["/provider_orders/stand_alone_issue_response", "Issue Response"],
  ["/provider_orders/patient-check-in-request", "Patient Check-In"],
  ["/provider_orders/signature-received", "Signature Received"],
  ["/provider_orders/review-unsigned-orders", "Review Unsigned Orders"],
  ["/provider_orders/doctor-signature", "Doctor Signature"],
  ["/provider_orders/index", "Orders"],
  ["/provider_orders/index/:newOrderNumber", "Orders"],
  ["/provider_orders/new", "New Order"],
  ["/provider_orders/new_home_health_order", "New Home Health Order"],
  ["/provider_order_list/index", "Orders"],
  ["/provider_order_list/index/:newOrderNumber", "Orders"],
  ["/provider-settings/*", "Settings"],
  ["/manufacturer_order_list/*", "Manufacturer Orders"],
  ["/hand_off_partner_order_list/*", "Partner Orders"],
  ["/analytics/*", "Analytics"],
];

/**
 * Sets `document.title` to `${title} - Verse Medical` for the lifetime of the
 * caller. Use this in components rendered for routes where the title is
 * dynamic (e.g. order pages where the title includes the order number). Pair
 * with an entry in `PER_PAGE_TITLE_PATTERNS` so `useRouteTitle` defers.
 */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} - ${SUFFIX}`;
  }, [title]);
}

/**
 * Updates `document.title` from `ROUTE_TITLES` whenever the location changes.
 * Skips paths handled by per-page `useDocumentTitle` so it does not clobber
 * dynamic titles set by route components. Call once at the App root.
 */
export function useRouteTitle(): void {
  const location = useLocation();
  useEffect(() => {
    const handledPerPage = PER_PAGE_TITLE_PATTERNS.some((pattern) =>
      matchPath({ path: pattern, end: true }, location.pathname),
    );
    if (handledPerPage) return;

    const match = ROUTE_TITLES.find(([pattern]) =>
      matchPath({ path: pattern, end: true }, location.pathname),
    );
    document.title = match ? `${match[1]} - ${SUFFIX}` : SUFFIX;
  }, [location.pathname]);
}
